"use client";

import { useSession } from "next-auth/react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { usePromotionStart } from "@/lib/planner/usePromotionStart";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePlannerChat } from "@/hooks/client/usePlannerChat";
import MessageThread from "./MessageThread";
import PlannerSidebar from "./PlannerSidebar";
import TravelerProfilePanel from "./TravelerProfilePanel";
import WelcomeScreen, {
  buildConciergeServices,
  DEFAULT_PROMPTS,
} from "./WelcomeScreen";
import { CloseButton } from "@/components/ui/BackButton";
import { cn } from "@/lib/utils";
import { INITIAL_PROMPTS_API } from "@/app/(app)/[bookingType]/search/researcher/components/constants";
import {
  ArrowUp,
  MessageSquareText,
  Notebook,
  Plus,
  Send,
  Square,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextArea } from "@/components/ui/textarea";
import { reportError } from "@/lib/apiUtils";
import { errorToast } from "@/lib/toast";
import { capturePlannerFrontendError } from "@/lib/planner/telemetry";

const MAX_SUGGESTED_PROMPTS = 5;

const GENERAL_CONCIERGE_PROMPTS = [
  "Tell me what you can help me plan as a traveler",
];

const DESTINATION_FALLBACK_PROMPTS = [
  "Help me choose a destination for a weekend trip",
  "Find the best hotel for a trip I'm planning",
  "Show me how to compare hotel options and rates",
];
const STICK_TO_BOTTOM_THRESHOLD_PX = 96;

function mergeSuggestedPrompts(personalizedPrompts: string[]) {
  const normalizedPersonalizedPrompts = personalizedPrompts
    .map((prompt) => prompt.trim())
    .filter(Boolean);

  return Array.from(
    new Set([
      ...GENERAL_CONCIERGE_PROMPTS,
      ...normalizedPersonalizedPrompts,
      ...DESTINATION_FALLBACK_PROMPTS,
    ])
  ).slice(0, MAX_SUGGESTED_PROMPTS);
}

interface PlannerChatProps {
  reviewBookingId?: string;
  /** Text to place in the composer on mount (never sent automatically). Ignored when a session is loading. */
  initialPrompt?: string;
}

export default function PlannerChat(props: PlannerChatProps) {
  const { data: session } = useSession();
  return (
    <PlannerChatContent key={session?.user.id ?? "signed-out"} {...props} />
  );
}
function PlannerChatContent({
  reviewBookingId,
  initialPrompt,
}: PlannerChatProps) {
  const { data: authSession, status: authStatus } = useSession();
  const promotionsEnabled = useFeatureFlagEnabled("email-promotions") === true;
  const searchParams = useSearchParams();
  const urlSessionId = searchParams.get("session");
  const promotionIntent = searchParams.get("intent");
  const promotionRequestId = searchParams.get("requestId");
  const {
    turns,
    isStreaming,
    queuedMessages,
    currentSessionId,
    sessionLoading,
    loadingSessionId,
    sessions,
    sessionsLoading,
    sessionsError,
    profileUpdateCount,
    sendMessage,
    stopStreaming,
    updateQueuedMessage,
    removeQueuedMessage,
    loadSession,
    loadSessions,
    newChat,
    deleteSession,
  } = usePlannerChat({
    prewarmOnMount:
      !urlSessionId && !reviewBookingId && promotionIntent !== "promotions",
  });

  const promotionStart = usePromotionStart({
    intent: promotionIntent,
    requestId: promotionRequestId,
    existingSession: urlSessionId,
    currentSession: currentSessionId,
    ready:
      authStatus === "authenticated" &&
      Boolean(authSession?.user.id) &&
      promotionsEnabled &&
      !sessionLoading &&
      !isStreaming &&
      !reviewBookingId,
    send: sendMessage,
  });
  // A URL session or a review handoff owns the first message; the prefill only applies to a fresh chat.
  const [inputText, setInputText] = useState(() =>
    initialPrompt && !urlSessionId && !reviewBookingId ? initialPrompt : ""
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [personalizedPrompts, setPersonalizedPrompts] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const previousScrollTopRef = useRef(0);
  const scrollFrameRef = useRef<number | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const loadedUrlSessionRef = useRef<string | null>(null);

  const replaceSessionUrl = useCallback(
    (sessionId: string | null) => {
      const nextParams = new URLSearchParams(searchParams.toString());
      if (sessionId) {
        nextParams.set("session", sessionId);
      } else {
        nextParams.delete("session");
      }
      // One-shot intents: once a session exists (or a new chat starts) a reload must not replay them.
      nextParams.delete("intent");
      nextParams.delete("requestId");
      nextParams.delete("prompt");
      const query = nextParams.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, {
        scroll: false,
      });
    },
    [pathname, router, searchParams]
  );

  const handleNewChat = useCallback(() => {
    newChat();
    replaceSessionUrl(null);
  }, [newChat, replaceSessionUrl]);

  // Load past sessions on mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    let cancelled = false;

    const loadPersonalizedPrompts = async () => {
      try {
        const response = await fetch(INITIAL_PROMPTS_API);
        if (!response.ok) return;

        const data = (await response.json()) as { samplePrompts?: unknown };
        if (!cancelled && Array.isArray(data.samplePrompts)) {
          setPersonalizedPrompts(
            data.samplePrompts.filter(
              (prompt): prompt is string => typeof prompt === "string"
            )
          );
        }
      } catch {
        if (!cancelled) {
          setPersonalizedPrompts([]);
        }
      }
    };

    loadPersonalizedPrompts();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!urlSessionId || loadedUrlSessionRef.current === urlSessionId) return;
    if (urlSessionId === currentSessionId) {
      loadedUrlSessionRef.current = urlSessionId;
      return;
    }
    loadedUrlSessionRef.current = urlSessionId;
    loadSession(urlSessionId);
  }, [currentSessionId, loadSession, urlSessionId]);

  useEffect(() => {
    if (!currentSessionId || currentSessionId === urlSessionId) return;
    loadedUrlSessionRef.current = currentSessionId;
    replaceSessionUrl(currentSessionId);
  }, [currentSessionId, replaceSessionUrl, urlSessionId]);

  // Handle post-trip review entry point
  useEffect(() => {
    if (!reviewBookingId) return;
    const createReviewSession = async () => {
      try {
        const response = await fetch("/api/planner/review", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ booking_id: reviewBookingId }),
        });
        if (!response.ok) {
          const err = new Error(`review_session failed: ${response.status}`);
          errorToast(
            "Couldn't start your review.",
            "Try opening the booking again in a moment."
          );
          reportError(err);
          capturePlannerFrontendError({
            session_id: null,
            location: "create_review_session",
            message: err.message,
            status: response.status,
          });
          return;
        }
        const data = await response.json();
        if (data.id) {
          loadSession(data.id);
        }
      } catch (err) {
        const error = err as Error;
        errorToast(
          "Couldn't start your review.",
          "Check your connection and try again."
        );
        reportError(error);
        capturePlannerFrontendError({
          session_id: null,
          location: "create_review_session",
          message: error.message,
        });
      }
    };
    createReviewSession();
  }, [reviewBookingId, loadSession]);

  const scheduleScrollToBottom = useCallback(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    if (scrollFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      if (shouldStickToBottomRef.current) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
      scrollFrameRef.current = null;
    });
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    const scrollingUp =
      scrollContainer.scrollTop < previousScrollTopRef.current;
    previousScrollTopRef.current = scrollContainer.scrollTop;
    const distanceFromBottom =
      scrollContainer.scrollHeight -
      scrollContainer.scrollTop -
      scrollContainer.clientHeight;
    shouldStickToBottomRef.current = scrollingUp
      ? false
      : distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD_PX;
  }, []);

  const handleStreamingTextReveal = useCallback(() => {
    if (!shouldStickToBottomRef.current) return;
    scheduleScrollToBottom();
  }, [scheduleScrollToBottom]);

  // Accordion animation and arriving hotel content change height after the turn
  // render. Follow those changes only while the reader is already at the bottom.
  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(handleStreamingTextReveal);
    observer.observe(content);
    return () => observer.disconnect();
  }, [handleStreamingTextReveal]);

  const stickToLatestMessage = useCallback(() => {
    shouldStickToBottomRef.current = true;
    scheduleScrollToBottom();
  }, [scheduleScrollToBottom]);

  // Follow streaming output only while the user is already reading the bottom.
  useEffect(() => {
    if (!shouldStickToBottomRef.current) return;
    scheduleScrollToBottom();
  }, [scheduleScrollToBottom, turns]);

  useEffect(
    () => () => {
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    []
  );

  // Restore focus after streaming finishes so the user can type immediately
  const prevIsStreaming = useRef(false);
  useEffect(() => {
    if (prevIsStreaming.current && !isStreaming) {
      textareaRef.current?.focus();
    }
    prevIsStreaming.current = isStreaming;
  }, [isStreaming]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [inputText]);

  const hasMessages = turns.length > 0;
  const isLoadingUrlSession =
    !!urlSessionId &&
    sessionLoading &&
    loadingSessionId === urlSessionId &&
    currentSessionId !== urlSessionId;
  const inputDisabled = isLoadingUrlSession;
  const sendDisabled = isLoadingUrlSession;
  const suggestedPrompts = useMemo(
    () => mergeSuggestedPrompts(personalizedPrompts),
    [personalizedPrompts]
  );

  const conciergeServices = useMemo(() => buildConciergeServices(), []);
  const mobileSuggestions = [
    ...suggestedPrompts
      .filter((prompt) => !DEFAULT_PROMPTS.has(prompt))
      .slice(0, 2)
      .map((prompt) => ({ label: prompt, prompt })),
    ...conciergeServices.map(({ prompt }, index) => ({
      label: ["Plan a new trip", "Find a hotel", "Compare points and cash"][
        index
      ],
      prompt,
    })),
  ].slice(0, 3);

  const handleSubmit = () => {
    if (!inputText.trim() || sendDisabled) return;
    sendMessage(inputText);
    stickToLatestMessage();
    setInputText("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (inputDisabled) return;
    setInputText(suggestion);
    textareaRef.current?.focus();
  };

  return (
    <div className="flex h-[calc(100dvh-var(--status-banner-height,0px))] overflow-hidden md:h-[calc(100svh-var(--header-offset))]">
      {/* Sidebar */}
      <PlannerSidebar
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        sessionsError={sessionsError}
        currentSessionId={currentSessionId}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSelectSession={(id) => {
          replaceSessionUrl(id);
          loadSession(id);
          setSidebarOpen(false);
        }}
        onNewChat={() => {
          handleNewChat();
          setSidebarOpen(false);
        }}
        onDeleteSession={deleteSession}
        onRetryLoadSessions={loadSessions}
      />

      {/* Main content */}
      <div
        data-testid="planner-main-column"
        className="flex min-w-0 flex-1 flex-col bg-background md:bg-gradient-to-b md:from-brand-gold/5 md:via-background md:to-background"
      >
        {promotionStart.failed && (
          <div role="status" className="px-4 py-3">
            <p className="text-sm">
              We couldn&apos;t start your promotions conversation.
            </p>
            <Button variant="ghost" size="sm" onClick={promotionStart.retry}>
              Retry promotions
            </Button>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/80 bg-background/80 px-4 py-3 backdrop-blur-sm">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold text-foreground md:text-lg md:tracking-tight">
              Gondola Concierge
            </h1>
          </div>
          <div className="flex shrink-0 items-center gap-1 md:hidden">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open conversations"
            >
              <MessageSquareText className="size-4 text-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleNewChat}
              disabled={!hasMessages && !isStreaming}
              aria-label="Start new chat"
            >
              <Plus className="size-4 text-foreground" />
            </Button>
            <CloseButton
              size="sm"
              href="/profile"
              ariaLabel="Close concierge"
              iconContainerClassName="bg-transparent hover:bg-muted"
            />
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setProfileOpen(!profileOpen)}
            aria-label={
              profileOpen ? "Close traveler profile" : "Open traveler profile"
            }
            aria-pressed={profileOpen}
            className="hidden text-muted-foreground hover:text-foreground md:inline-flex"
          >
            <Notebook className="h-4 w-4" />
          </Button>
        </div>

        {/* Messages area */}
        <div
          ref={scrollRef}
          data-testid="planner-message-scroll"
          className={cn(
            "min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-5 md:px-6 md:py-6",
            !hasMessages && "flex flex-col md:block"
          )}
          onScroll={handleMessagesScroll}
        >
          <div
            ref={contentRef}
            data-testid="planner-message-content"
            className={cn(
              "mx-auto w-full max-w-3xl",
              !hasMessages && "my-auto md:my-0"
            )}
          >
            {hasMessages ? (
              <MessageThread
                onRetryRates={sendMessage}
                turns={turns}
                onNewChat={handleNewChat}
                isStreaming={isStreaming}
                onStreamingTextReveal={handleStreamingTextReveal}
              />
            ) : (
              <WelcomeScreen
                suggestions={suggestedPrompts}
                onSuggestionClick={handleSuggestionClick}
              />
            )}
          </div>
        </div>

        {/* Input area */}
        <div
          data-testid="planner-composer-frame"
          className="shrink-0 bg-background px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:border-t md:border-border/80 md:bg-background/85 md:px-4 md:py-4 md:backdrop-blur-sm"
        >
          <div
            data-testid="planner-composer-content"
            className="mx-auto w-full max-w-3xl"
          >
            {!hasMessages && !inputText.trim() ? (
              <div
                aria-label="Suggested messages"
                className="mb-2 flex flex-wrap gap-2 px-1 md:hidden"
              >
                {mobileSuggestions.map(({ label, prompt }) => (
                  <Button
                    key={prompt}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestionClick(prompt)}
                    className="h-auto max-w-full rounded-full border-border bg-card px-3 py-2 text-sm font-medium"
                  >
                    <span className="truncate">{label}</span>
                  </Button>
                ))}
              </div>
            ) : null}
            {queuedMessages.length > 0 ? (
              <QueuedMessages
                messages={queuedMessages}
                onChangeMessage={updateQueuedMessage}
                onRemoveMessage={removeQueuedMessage}
              />
            ) : null}
            <div
              data-testid="planner-composer-shell"
              className="flex min-h-12 min-w-0 items-end gap-2 rounded-3xl border border-input bg-card px-3 py-1.5 transition-[border-color,box-shadow] duration-150 ease-signature focus-within:border-brand-gold/60 focus-within:shadow-md md:rounded-2xl md:p-2 md:shadow-sm"
            >
              <TextArea
                ref={textareaRef}
                value={inputText}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setInputText(e.target.value)
                }
                onKeyDown={handleKeyDown}
                placeholder="Anything travel"
                aria-label="Message concierge"
                disabled={inputDisabled}
                rows={1}
                className="max-h-32 min-h-10 min-w-0 flex-1 resize-none border-0 bg-transparent px-0 py-2 text-base leading-5 text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 disabled:opacity-50 md:max-h-52 md:min-h-0 md:px-2 md:py-1.5 md:text-sm"
              />
              {isStreaming && !inputText.trim() ? (
                <Button
                  size="icon-sm"
                  onClick={stopStreaming}
                  aria-label="Stop generating"
                  className="size-9 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 md:size-8 md:rounded-lg"
                >
                  <Square className="h-3.5 w-3.5" />
                </Button>
              ) : null}
              {!isStreaming || inputText.trim() ? (
                <Button
                  size="icon-sm"
                  onClick={handleSubmit}
                  disabled={!inputText.trim() || sendDisabled}
                  aria-label={isStreaming ? "Queue message" : "Send message"}
                  className="size-9 rounded-full md:size-8 md:rounded-lg"
                >
                  <ArrowUp className="size-4 md:hidden" />
                  <Send className="hidden h-3.5 w-3.5 md:block" />
                </Button>
              ) : null}
            </div>
            {isLoadingUrlSession ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Loading conversation...
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Traveler profile panel */}
      <TravelerProfilePanel
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        refreshTrigger={profileUpdateCount}
      />
    </div>
  );
}

function QueuedMessages({
  messages,
  onChangeMessage,
  onRemoveMessage,
}: {
  messages: string[];
  onChangeMessage: (index: number, message: string) => void;
  onRemoveMessage: (index: number) => void;
}) {
  return (
    <div
      data-testid="planner-queued-messages"
      className="mb-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
    >
      <p>Messages to be submitted after this response</p>
      <div className="mt-2 space-y-2">
        {messages.map((message, index) => (
          <div key={index} className="flex min-w-0 items-start gap-2">
            <TextArea
              aria-label={`Queued message ${index + 1}`}
              value={message}
              rows={1}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                onChangeMessage(index, e.target.value)
              }
              className="min-h-8 min-w-0 flex-1 resize-none rounded-md border-input bg-background px-2 py-1 text-xs text-foreground shadow-none focus-visible:ring-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove queued message ${index + 1}`}
              onClick={() => onRemoveMessage(index)}
              className="h-8 w-8 shrink-0 rounded-md text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
